import { arrayEquals } from "../shared/array-equals";
import { ColumnDefinition, EntityInterface } from "./Table";
import { PermissionEntry } from "./User";

/**
 * Interface DTO for a user group
 */
export interface UserGroupDto {
    id: string;
    name: string;
    permissions: {
        id: number | null; // ID can be null for new permissions
        companyId: number; // Company ID to which the permission belongs
        permission: string; // The actual permission string
    }[];
}

/**
 * UserGroup class representing a user group
 */
export class UserGroup implements EntityInterface {
    /**
     * Constructor for the UserGroup class
     * 
     * @param name 
     */
    constructor(
        public id: string,
        public name: string,
        public permissions: PermissionEntry[] = [],
    ) { }

    /**
     * Creates a copy of the current UserGroup instance
     * 
     * @returns A copy of the current UserGroup instance
     */
    copyOf(): UserGroup {
        return new UserGroup(
            this.id,
            this.name,
            this.permissions.map(p => new PermissionEntry(p.id, p.companyId, p.permission)),
        );
    }

    /**
     * Return true if the current UserGroup instance is equal to the given userGroup
     * 
     * @param other 
     * @returns 
     */
    equals(other: UserGroup): boolean {
        if (!other) return false;

        return this.id === other.id &&
            this.name === other.name &&
            arrayEquals(this.permissions, other.permissions);
    }

    /**
     * Returns a string representation of the UserGroup instance for filtering
     * 
     * @param defs The column definitions to use for formatting
     * @returns A string representation of the UserGroup instance
     */
    toString(defs: ColumnDefinition<any>[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }
}

/** Mapper for converting between UserGroupDto and UserGroup */
export class UserGroupMapper {
    static toDto(userGroup: UserGroup): UserGroupDto {
        return {
            id: userGroup.id,
            name: userGroup.name,
            permissions: userGroup.permissions.map(p => ({ id: p.id, companyId: p.companyId, permission: p.permission })),
        };
    }

    static toModel(dto: UserGroupDto): UserGroup {
        return new UserGroup(
            dto.id,
            dto.name,
            dto.permissions.map(p => new PermissionEntry(p.id, p.companyId, p.permission)),
        );
    }
}